/** Pending Basecamp todo create shown as Create / Cancel in chat. */

import { looksLikeHtml as looksLikeHtmlShared, normalizeBasecampHtml } from '@/lib/basecamp-html'

export type BasecampCreateDraftStatus = 'pending' | 'created' | 'dismissed'

export type BasecampCreateTodoDraft = {
  todolistUrl: string
  content: string
  description?: string
  due_on?: string
  projectId?: string
  todolistId?: string
  accountId?: string
  status?: BasecampCreateDraftStatus
  createdUrl?: string
}

const STORAGE_PREFIX = 'flownotes_basecamp_create_draft:'

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function optionalString(value: unknown): string | undefined {
  if (value == null) return undefined
  const text = String(value).trim()
  return text ? text : undefined
}

function parseStatus(value: unknown): BasecampCreateDraftStatus | undefined {
  if (value === 'pending' || value === 'created' || value === 'dismissed') return value
  return undefined
}

/** True when notes should render as HTML (same as Basecamp notes draft). */
export function looksLikeHtml(value: string): boolean {
  return looksLikeHtmlShared(value)
}

export function createDraftFingerprint(draft: Pick<BasecampCreateTodoDraft, 'todolistUrl' | 'content'>): string {
  return `${draft.todolistUrl}::${draft.content}`
}

/** Parse a proposed_basecamp_create SSE payload into a confirm-card draft. */
export function parseProposedBasecampCreateEvent(
  parsed: Record<string, unknown>,
): BasecampCreateTodoDraft | null {
  const nested = asRecord(parsed.wouldCreate)
  const src = nested ?? parsed
  const todolistUrl = optionalString(src.todolistUrl) || optionalString(parsed.todolistUrl)
  const content = optionalString(src.content) || optionalString(parsed.content)
  if (!todolistUrl || !content) return null
  const descriptionRaw = optionalString(src.description)
  return {
    todolistUrl,
    content,
    description: descriptionRaw ? normalizeBasecampHtml(descriptionRaw) : undefined,
    due_on: optionalString(src.due_on),
    projectId: optionalString(src.projectId),
    todolistId: optionalString(src.todolistId),
    accountId: optionalString(src.accountId),
    status: parseStatus(src.status) || parseStatus(parsed.status) || 'pending',
    createdUrl: optionalString(src.createdUrl) || optionalString(parsed.createdUrl),
  }
}

export function persistBasecampCreateDraft(
  conversationId: string | null | undefined,
  draft: BasecampCreateTodoDraft,
): void {
  if (!conversationId) return
  try {
    sessionStorage.setItem(STORAGE_PREFIX + conversationId, JSON.stringify(draft))
    if (draft.status === 'pending' || !draft.status) {
      sessionStorage.removeItem(doneKey(conversationId))
    }
    persistBasecampCreateOutcome(conversationId, draft)
  } catch {
    /* ignore quota / private mode */
  }
}

export function persistBasecampCreateOutcome(
  conversationId: string | null | undefined,
  draft: BasecampCreateTodoDraft,
): void {
  if (!conversationId) return
  if (!draft.status || draft.status === 'pending') return
  try {
    sessionStorage.setItem(
      outcomeKey(conversationId, createDraftFingerprint(draft)),
      JSON.stringify({ status: draft.status, createdUrl: draft.createdUrl }),
    )
    if (draft.status !== 'pending') {
      sessionStorage.setItem(doneKey(conversationId), '1')
    }
  } catch {
    /* ignore */
  }
}

export function applyBasecampCreateOutcome(
  conversationId: string | null | undefined,
  draft: BasecampCreateTodoDraft,
): BasecampCreateTodoDraft {
  if (!conversationId) return draft
  try {
    const raw = sessionStorage.getItem(outcomeKey(conversationId, createDraftFingerprint(draft)))
    if (!raw) return draft
    const parsed = JSON.parse(raw)
    const status = parseStatus(parsed?.status)
    if (!status) return draft
    return {
      ...draft,
      status,
      createdUrl: optionalString(parsed?.createdUrl) || draft.createdUrl,
    }
  } catch {
    return draft
  }
}

export function readPersistedBasecampCreateDraft(
  conversationId: string | null | undefined,
): BasecampCreateTodoDraft | null {
  if (!conversationId) return null
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + conversationId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const draft = asRecord(parsed) ? parseProposedBasecampCreateEvent(parsed) : null
    return draft ? applyBasecampCreateOutcome(conversationId, draft) : null
  } catch {
    return null
  }
}

export function clearPersistedBasecampCreateDraft(
  conversationId: string | null | undefined,
): void {
  if (!conversationId) return
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + conversationId)
  } catch {
    /* ignore */
  }
}

export function markBasecampCreateDraftConsumed(
  conversationId: string | null | undefined,
): void {
  if (!conversationId) return
  try {
    sessionStorage.setItem(doneKey(conversationId), '1')
    sessionStorage.removeItem(STORAGE_PREFIX + conversationId)
  } catch {
    /* ignore */
  }
}

export function isBasecampCreateDraftConsumed(
  conversationId: string | null | undefined,
): boolean {
  if (!conversationId) return false
  try {
    return sessionStorage.getItem(doneKey(conversationId)) === '1'
  } catch {
    return false
  }
}

function doneKey(conversationId: string): string {
  return `${STORAGE_PREFIX}${conversationId}:done`
}

function outcomeKey(conversationId: string, fingerprint: string): string {
  return `${STORAGE_PREFIX}${conversationId}:outcome:${fingerprint}`
}

/** Last pending create draft stored on assistant message metadata (survives reload). */
export function basecampCreateDraftFromMessages(
  messages: Array<{ role?: string; metadata?: Record<string, unknown> }>,
): BasecampCreateTodoDraft | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message?.role !== 'assistant') continue
    const raw = asRecord(message.metadata?.basecampCreateDraft)
    const draft = raw ? parseProposedBasecampCreateEvent(raw) : null
    if (draft) return draft
  }
  return null
}

export function restoreBasecampCreateDraft(
  conversationId: string | null | undefined,
  messages: Array<{ role?: string; metadata?: Record<string, unknown> }> = [],
): BasecampCreateTodoDraft | null {
  const fromMessages = basecampCreateDraftFromMessages(messages)
  if (fromMessages) {
    return applyBasecampCreateOutcome(conversationId, fromMessages)
  }
  return readPersistedBasecampCreateDraft(conversationId)
}
